
import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Icon } from '@/components/ui/Icon'
import { btn, card, cx, input, inputBase, label, notice, pageIntro, pageTitle } from '@/lib/ui'
import { ROLE_LABELS } from '@/lib/roles'
import type { InviteDTO, MemberDTO, WorkspaceRole } from '@/lib/types'
import { apiFetch, errorMessage } from '@/lib/client-api'

export function SettingsClient({
  workspace,
  role,
  currentUserId,
  initialMembers,
  initialInvites,
  apiBase,
}: {
  workspace: { id: string; name: string; slug: string }
  role: WorkspaceRole
  currentUserId: string
  initialMembers: MemberDTO[]
  initialInvites: InviteDTO[]
  apiBase: string
}) {
  const router = useRouter()
  const isOwner = role === 'OWNER'
  const canManage = role === 'OWNER' || role === 'ADMIN'
  const [members, setMembers] = useState(initialMembers)
  const [invites, setInvites] = useState(initialInvites)
  const [name, setName] = useState(workspace.name)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [inviteRole, setInviteRole] = useState<'MEMBER' | 'ADMIN'>('MEMBER')
  const [busy, setBusy] = useState(false)

  // apiFetch turns a 422 into the field messages zod actually produced,
  // instead of the flat "Validation failed" this used to show.
  async function call(path: string, init?: RequestInit) {
    setError(null)
    setMessage(null)
    return apiFetch(`${apiBase}${path}`, init)
  }

  async function rename(event: React.FormEvent) {
    event.preventDefault()
    try {
      await call('', { method: 'PATCH', body: JSON.stringify({ name }) })
      setMessage('Workspace renamed.')
      router.refresh()
    } catch (cause) {
      setError(errorMessage(cause, 'Could not rename'))
    }
  }

  async function changeRole(member: MemberDTO, nextRole: WorkspaceRole) {
    try {
      const updated = (await call(`/members/${member.userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: nextRole }),
      })) as MemberDTO
      setMembers((current) => current.map((row) => (row.userId === member.userId ? updated : row)))
    } catch (cause) {
      setError(errorMessage(cause, 'Could not change role'))
    }
  }

  async function removeMember(member: MemberDTO) {
    const self = member.userId === currentUserId
    if (!confirm(self ? `Leave ${workspace.name}?` : `Remove ${member.email} from ${workspace.name}?`)) return
    try {
      await call(`/members/${member.userId}`, { method: 'DELETE' })
      if (self) {
        router.push('/')
        return
      }
      setMembers((current) => current.filter((row) => row.userId !== member.userId))
    } catch (cause) {
      setError(errorMessage(cause, 'Could not remove member'))
    }
  }

  async function createInvite() {
    setBusy(true)
    try {
      const invite = (await call('/invites', {
        method: 'POST',
        body: JSON.stringify({ role: inviteRole, expiresInDays: 14 }),
      })) as InviteDTO
      setInvites((current) => [invite, ...current])
    } catch (cause) {
      setError(errorMessage(cause, 'Could not create invite'))
    } finally {
      setBusy(false)
    }
  }

  async function revokeInvite(invite: InviteDTO) {
    try {
      await call(`/invites/${invite.id}`, { method: 'DELETE' })
      setInvites((current) => current.filter((row) => row.id !== invite.id))
    } catch (cause) {
      setError(errorMessage(cause, 'Could not revoke invite'))
    }
  }

  async function copyInvite(invite: InviteDTO) {
    const url = `${window.location.origin}/invite/${invite.token}`
    try {
      await navigator.clipboard.writeText(url)
      setMessage('Invite link copied.')
    } catch {
      setMessage(url)
    }
  }

  async function deleteWorkspace() {
    const typed = prompt(
      `This deletes every project, device and inventory unit in ${workspace.name}. Type the workspace name to confirm.`,
    )
    if (typed !== workspace.name) return
    try {
      await call('', { method: 'DELETE' })
      router.push('/')
    } catch (cause) {
      setError(errorMessage(cause, 'Could not delete workspace'))
    }
  }

  const sectionTitle = 'text-base font-semibold tracking-tight'

  return (
    <div className="mx-auto w-full max-w-3xl space-y-5 px-5 py-8 sm:px-8 sm:py-10">
      <div>
        <h1 className={pageTitle}>Workspace settings</h1>
        <p className={pageIntro}>
          You are {ROLE_LABELS[role].toLowerCase()} of <span className="text-foreground">{workspace.name}</span>.
        </p>
      </div>

      {error && <p role="alert" className={notice.error}>{error}</p>}
      {message && <p className={cx('break-all', notice.success)}>{message}</p>}

      <section className={cx(card, 'p-5 sm:p-6')}>
        <h2 className={sectionTitle}>Name</h2>
        <form onSubmit={rename} className="mt-3 flex gap-2">
          <input
            className={input}
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={!isOwner}
            minLength={2}
            maxLength={60}
            aria-label="Workspace name"
          />
          {isOwner && (
            <button type="submit" className={cx(btn.base, btn.secondary)}>
              Save
            </button>
          )}
        </form>
        <p className="mt-2.5 font-mono text-[11px] text-muted">URL: /w/{workspace.slug}</p>
      </section>

      <section className={cx(card, 'p-5 sm:p-6')}>
        <h2 className={sectionTitle}>Members ({members.length})</h2>
        <ul className="mt-2 divide-y divide-[var(--border)]">
          {members.map((member) => {
            const self = member.userId === currentUserId
            const canChange = canManage && (role === 'OWNER' || member.role !== 'OWNER')
            return (
              <li key={member.userId} className="flex flex-wrap items-center gap-3 py-3">
                <span className="gradient-pastel grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[11px] font-semibold text-foreground/70">
                  {(member.name || member.email).slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {member.name || member.email}
                    {self && <span className="ml-1.5 text-xs font-normal text-muted">(you)</span>}
                  </p>
                  {member.name && <p className="truncate text-xs text-muted">{member.email}</p>}
                </div>
                {canChange ? (
                  <select
                    className={cx(inputBase, 'rounded-full py-1.5 text-xs')}
                    value={member.role}
                    onChange={(event) => changeRole(member, event.target.value as WorkspaceRole)}
                    aria-label={`Role for ${member.email}`}
                  >
                    {(role === 'OWNER' ? (['OWNER', 'ADMIN', 'MEMBER'] as const) : (['ADMIN', 'MEMBER'] as const)).map(
                      (value) => (
                        <option key={value} value={value}>
                          {ROLE_LABELS[value]}
                        </option>
                      ),
                    )}
                  </select>
                ) : (
                  <span className="rounded-full bg-[var(--surface-raised)] px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-muted">
                    {ROLE_LABELS[member.role]}
                  </span>
                )}
                {(self || canChange) && (
                  <button type="button" onClick={() => removeMember(member)} className={cx(btn.sm, btn.ghost)}>
                    {self ? 'Leave' : 'Remove'}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      </section>

      {canManage && (
        <section className={cx(card, 'p-5 sm:p-6')}>
          <h2 className={sectionTitle}>Invite links</h2>
          <p className="mt-1 text-sm text-muted">
            Anyone with a link can join with the role you choose. Links last 14 days; revoke one at any time.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div>
              <label className={label} htmlFor="invite-role">
                Role
              </label>
              <select
                id="invite-role"
                className={cx(inputBase, 'mt-1.5')}
                value={inviteRole}
                onChange={(event) => setInviteRole(event.target.value as 'MEMBER' | 'ADMIN')}
              >
                <option value="MEMBER">Member</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <button type="button" onClick={createInvite} disabled={busy} className={cx(btn.base, btn.primary)}>
              <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
              {busy ? 'Creating…' : 'Create invite link'}
            </button>
          </div>
          {invites.length > 0 && (
            <ul className="mt-4 divide-y divide-[var(--border)]">
              {invites.map((invite) => (
                <li key={invite.id} className="flex flex-wrap items-center gap-3 py-3">
                  <code className="min-w-0 flex-1 truncate font-mono text-xs text-muted">
                    /invite/{invite.token}
                  </code>
                  <span className="rounded-full bg-[var(--surface-raised)] px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-muted">
                    {ROLE_LABELS[invite.role]}
                  </span>
                  <span className="text-[11px] text-muted">
                    used {invite.useCount}
                    {invite.maxUses ? `/${invite.maxUses}` : ''} · expires{' '}
                    {new Date(invite.expiresAt).toLocaleDateString()}
                  </span>
                  <button type="button" onClick={() => copyInvite(invite)} className={cx(btn.sm, btn.secondary)}>
                    <Icon name="copy" className="h-3.5 w-3.5" />
                    Copy link
                  </button>
                  <button type="button" onClick={() => revokeInvite(invite)} className={cx(btn.sm, btn.ghost)}>
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {isOwner && (
        <section className={cx(card, 'border-danger-line p-5 sm:p-6')}>
          <h2 className={cx(sectionTitle, 'text-danger')}>Danger zone</h2>
          <p className="mt-1 text-sm text-muted">
            Deleting the workspace removes all of its projects, devices and inventory for every member.
          </p>
          <button type="button" onClick={deleteWorkspace} className={cx(btn.base, btn.danger, 'mt-4')}>
            <Icon name="trash" className="h-4 w-4" />
            Delete workspace
          </button>
        </section>
      )}
    </div>
  )
}
